// Share / revoke a public link. Unlike ShareTasks (a snapshot with no revoke),
// these links are LIVE pointers with at most one row per key, so the modal's
// first job on every open is to find out which of two worlds it is in: no link
// yet (offer to create one) or link active (show it, offer to revoke).
// Re-queried on each open rather than cached -- a rename can move the share's
// key underneath us, and another tab may have created or revoked the link.
//
// That five-state machine IS the feature's rules, and it is shared by both
// kinds of trip (ShareTripModal, ShareLogbookTripModal). Everything else -- the
// lookup, the create, the URL builder, the prose -- is a parameter, because the
// two differ only in what they are about. A second copy of the machine would be
// a second place for "re-query on open" and "at most one row" to be got wrong.
//
// Props:
//   title       - the dialog's heading.
//   description - shown above Create; what the reader is about to publish.
//   activeNote  - shown under the copy box once a link exists. Optional.
//   revokedNote - the toast after a revoke. Optional; defaults to the trip
//                 wording, so both trip callers stand unchanged.
//   load        - () => Promise<row | null>. Run on every open.
//   create      - () => Promise<row>.
//   revoke      - (row) => Promise<any>.
//   urlFor      - (row) => the absolute URL to show and copy.
//   activeIntro - the line above the copy box once a link exists. It DEFAULTS to
//                 the read-only sentence, which is what every trip share has
//                 always said and is still true of one -- but a Captain's Log
//                 share can now accept photos back, and a dialog that calls such
//                 a link read-only while handing it out is simply wrong. A prop
//                 rather than a branch on a flag, because this component knows
//                 nothing about what its caller's link can do.
//   extras      - optional (row | null) => node, rendered under the body. A
//                 RENDER FUNCTION rather than a node plus an `onLoaded` callback,
//                 so a caller that needs the loaded row cannot end up holding a
//                 second copy of it to keep in step with this machine's own
//                 `share` state. ShareTripModal omits it and its suite stands
//                 unchanged, which is the proof the default is absent.

import { useState, useEffect, useCallback } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useStatusMessage } from '../../components';

const stateLoading = 0;
const stateNoLink = 1;
const stateCreating = 2;
const stateActive = 3;
const stateRevoking = 4;

function NoLinkBody({ description, onCreateLink }) {
  return (
    <div className="text-center">
      <p>{description}</p>
      <button className="btn btn-primary" onClick={onCreateLink}>
        <i className="fa-solid fa-link me-2"></i>
        Create Shareable Link
      </button>
    </div>
  );
}

const DEFAULT_REVOKED_NOTE =
  'Link revoked. Anyone holding it now sees that the trip is no longer shared.';

function ActiveLinkBody({ shareUrl, activeIntro, activeNote, copied, onCopy, onRevoke }) {
  return (
    <div>
      <p>{activeIntro}</p>
      <div className="input-group mb-3">
        <input
          type="text"
          className="form-control"
          value={shareUrl}
          readOnly
          onClick={(e) => e.target.select()}
        />
        <button className="btn btn-primary" onClick={onCopy}>
          <i className={`fa-regular ${copied ? 'fa-check' : 'fa-clipboard'} me-1`}></i>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <p className="text-muted small mb-3">{activeNote}</p>
      <div className="text-center">
        <button className="btn btn-outline-danger" onClick={onRevoke}>
          Revoke Link
        </button>
      </div>
    </div>
  );
}

export default function ShareLinkModal({
  show,
  onHide,
  title,
  description,
  activeIntro = 'Anyone with this link can view this trip — read-only, no account needed:',
  activeNote,
  revokedNote,
  load,
  create,
  revoke,
  urlFor,
  extras,
}) {
  const [StatusMessage, { showText, showError, hideStatus }] = useStatusMessage();
  const [state, setState] = useState(stateLoading);
  const [share, setShare] = useState(null);
  const [copied, setCopied] = useState(false);

  // Look up the existing share on every open (see the header comment).
  //
  // `load` is deliberately NOT in the deps: a caller's inline arrow is a new
  // identity on every render, which would re-run this on each one -- a lookup
  // per keystroke anywhere in the host. `show` going true is the event that
  // matters, and the caller re-renders the modal closed before changing what it
  // is about.
  useEffect(() => {
    if (!show) return undefined;
    let mounted = true;
    setState(stateLoading);
    setShare(null);
    setCopied(false);
    Promise.resolve()
      .then(load)
      .then((row) => {
        if (!mounted) return;
        setShare(row);
        setState(row ? stateActive : stateNoLink);
      })
      .catch((err) => {
        if (!mounted) return;
        showError(err);
        setState(stateNoLink);
      });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, showError]);

  const handleClose = useCallback(() => {
    hideStatus();
    onHide();
  }, [onHide, hideStatus]);

  const handleCreateLink = useCallback(async () => {
    try {
      hideStatus();
      setState(stateCreating);
      const created = await create();
      setShare(created);
      setState(stateActive);
    } catch (err) {
      showError(err);
      setState(stateNoLink);
    }
  }, [create, showError, hideStatus]);

  const handleRevoke = useCallback(async () => {
    try {
      hideStatus();
      setState(stateRevoking);
      await revoke(share);
      setShare(null);
      setState(stateNoLink);
      showText(revokedNote ?? DEFAULT_REVOKED_NOTE);
    } catch (err) {
      showError(err);
      setState(stateActive);
    }
  }, [revoke, share, showText, showError, hideStatus, revokedNote]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(urlFor(share));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showError('Failed to copy to clipboard');
    }
  }, [urlFor, share, showError]);

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <StatusMessage />
        {state === stateLoading && (
          <div className="text-center text-primary">
            <i className="fa fa-spinner fa-spin me-2"></i>
            Checking for an existing link...
          </div>
        )}
        {state === stateNoLink && (
          <NoLinkBody description={description} onCreateLink={handleCreateLink} />
        )}
        {state === stateCreating && (
          <div className="text-center text-primary">
            <i className="fa fa-spinner fa-spin me-2"></i>
            Creating link...
          </div>
        )}
        {state === stateActive && (
          <ActiveLinkBody
            shareUrl={urlFor(share)}
            activeIntro={activeIntro}
            activeNote={activeNote}
            copied={copied}
            onCopy={handleCopy}
            onRevoke={handleRevoke}
          />
        )}
        {state === stateRevoking && (
          <div className="text-center text-primary">
            <i className="fa fa-spinner fa-spin me-2"></i>
            Revoking link...
          </div>
        )}

        {/* Only in the two SETTLED states. Mid-create or mid-revoke the row is
            about to change underneath anything drawn from it, and a control
            offering to change a setting on a link that is being deleted is worse
            than one that is briefly absent. */}
        {extras && (state === stateNoLink || state === stateActive) && extras(share)}
      </Modal.Body>
    </Modal>
  );
}
